// src/components/team/TeamActivityFeed.jsx
//
// Per-team recent-activity timeline for the coach Overview. The
// "assistant coach" answer to "what's been happening with my team?"
//
// v1 scope (May 2026): registrations only. Each new registration
// creates a timeline entry. Payment-received entries are inferred
// from a registration whose status is 'paid' AND createdAt is
// recent — both events show as separate items so the coach sees
// "Sarah Chen registered" + "Sarah Chen paid $150" as two distinct
// signals.
//
// Future sources to add (queued, not in v1):
//   - roster changes (player added / removed manually)
//   - schedule changes (event added / cancelled / rescheduled)
//   - weather alerts posted
//   - broadcasts sent
//   - document uploads
//
// This component takes its inputs as PROPS-FREE — it queries Firestore
// itself, scoped to the teamId passed in. Same pattern as
// TeamCommunicationHub. Renders null when there's no recent activity
// (silence is better than nag for inactive teams).

import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  collection, query, where, orderBy, limit, onSnapshot,
} from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import {
  ClockIcon,
  UserPlusIcon,
  CurrencyDollarIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

const HEAD_FONT = "'Barlow Condensed', sans-serif";
const ACTIVITY_LOOKBACK_DAYS = 14; // last 2 weeks
const MAX_ITEMS = 8;

function tsToMs(ts) {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (ts.seconds) return ts.seconds * 1000;
  return 0;
}

// Compact "5m / 2h / 3d" relative-time formatter. Same pattern as
// TeamCommunicationHub's formatRelative — kept inline here to avoid
// over-eagerly extracting a shared util before there are 3+ consumers.
function formatRelative(ts) {
  const ms = tsToMs(ts);
  if (!ms) return '';
  const diff = Date.now() - ms;
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function fmtMoney(cents) {
  if (!cents) return '$0';
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function playerNameFor(reg) {
  return (
    reg.playerName ||
    [reg.playerFirstName, reg.playerLastName].filter(Boolean).join(' ').trim() ||
    'A player'
  );
}

function amountFor(reg) {
  return reg?.pricing?.finalAmount ?? reg?.amountDue ?? 0;
}

export default function TeamActivityFeed({ teamId, activeSeason }) {
  const [recentRegs, setRecentRegs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!teamId) return;
    setLoading(true);

    // Subscribe to recent registrations for this team. We query by
    // teamId only (no seasonId) so we capture activity from BOTH the
    // active season and any side-program registrations the team is
    // attached to (rare but real). A status filter would miss
    // 'pending_payment' transitions which are also useful signal.
    const cutoff = new Date(Date.now() - ACTIVITY_LOOKBACK_DAYS * 24 * 60 * 60 * 1000);

    const q = query(
      collection(db, 'registrations'),
      where('teamId', '==', teamId),
      orderBy('createdAt', 'desc'),
      limit(MAX_ITEMS * 2), // over-fetch since some will be filtered by date or duplicates
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((r) => {
            const ms = tsToMs(r.createdAt);
            return ms > 0 && ms >= cutoff.getTime();
          });
        setRecentRegs(rows);
        setLoading(false);
      },
      (err) => {
        if (import.meta.env.DEV) {
          console.warn('[TeamActivityFeed] subscription failed', err);
        }
        setLoading(false);
      }
    );

    return unsub;
  }, [teamId]);

  // Build the activity items. Each registration becomes ONE entry —
  // payment-on-paid is a future enhancement (would require comparing
  // before/after status, which requires a separate trigger or
  // historical snapshot. For v1, "Sarah Chen registered" covers both
  // free + paid; the amount is shown inline when > 0.
  const items = useMemo(() => {
    return recentRegs.slice(0, MAX_ITEMS).map((reg) => {
      const name = playerNameFor(reg);
      const amount = amountFor(reg);
      const isPaid = reg.status === 'paid' || reg.status === 'confirmed';
      const seasonName = reg.seasonName || activeSeason?.name || 'this season';

      // Two visual variants: paid registrations get a money icon (the
      // implicit signal: "you got money"); unpaid get the user-plus icon
      // (just "someone signed up, no payment yet"). Coaches care more
      // about the money signal — visually weight it heavier.
      if (isPaid && amount > 0) {
        return {
          id: reg.id,
          icon: <CurrencyDollarIcon className="h-4 w-4 text-[#5a8a1f]" />,
          iconBg: 'bg-[#b5f03d]/20',
          title: <><strong className="text-[#0d1f14]">{name}</strong> registered & paid <span className="text-[#3d6010] font-semibold">{fmtMoney(amount)}</span></>,
          sub: seasonName,
          ts: reg.createdAt,
          href: reg.seasonId ? `/seasons/${reg.seasonId}/registrations` : null,
        };
      }
      return {
        id: reg.id,
        icon: <UserPlusIcon className="h-4 w-4 text-zinc-500" />,
        iconBg: 'bg-zinc-100',
        title: <><strong className="text-[#0d1f14]">{name}</strong> registered{amount > 0 ? <> · <span className="text-amber-700">payment pending</span></> : null}</>,
        sub: seasonName,
        ts: reg.createdAt,
        href: reg.seasonId ? `/seasons/${reg.seasonId}/registrations` : null,
      };
    });
  }, [recentRegs, activeSeason]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)]">
        <div className="flex items-center gap-2 mb-3">
          <ClockIcon className="h-3.5 w-3.5 text-[#5a8a1f]" />
          <p
            className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
            style={{ fontFamily: HEAD_FONT }}
          >
            Recent activity
          </p>
        </div>
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-12 rounded-lg bg-zinc-100 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    // Render NULL on inactive teams. The "no recent activity" empty
    // state would be visual debt — coaches with established teams
    // mid-off-season would see a constant "nothing happening" card.
    // Silence is better than nag.
    return null;
  }

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)]">
      <div className="flex items-center gap-2 mb-3">
        <ClockIcon className="h-3.5 w-3.5 text-[#5a8a1f]" />
        <p
          className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
          style={{ fontFamily: HEAD_FONT }}
        >
          Recent activity
        </p>
      </div>

      <ul className="space-y-2">
        {items.map((item) => {
          const inner = (
            <div className="flex items-center gap-3 rounded-lg px-2 py-2 -mx-2 hover:bg-zinc-50 transition-colors group">
              <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${item.iconBg}`}>
                {item.icon}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-zinc-700 leading-snug">
                  {item.title}
                </p>
                <p className="text-[11px] text-zinc-500 mt-0.5 truncate">
                  {item.sub}
                </p>
              </div>
              <div className="shrink-0 flex items-center gap-1 text-[11px] text-zinc-400">
                <span>{formatRelative(item.ts)}</span>
                {item.href && (
                  <ArrowRightIcon className="h-3 w-3 text-zinc-300 group-hover:text-[#5a8a1f] transition-colors" />
                )}
              </div>
            </div>
          );
          return (
            <li key={item.id}>
              {item.href ? <Link to={item.href}>{inner}</Link> : inner}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
